import ProfileLogo from '@components/ProfileLogo'
import { StyledAltLogoWrapper } from '@components/ProfileLogo/index.styles'
import InputText from '@components/formControls/InputText'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { css, styled } from '@mui/material/styles'
import { colors, componentSizes } from '@theme/variables'

import SortButton from './SortButton'

export const TableWrapper = styled(Box)`
  position: relative;
  width: 100%;
  margin-bottom: 5px;
`

export const StyledTableRow = styled(TableRow)<{ $isActive: boolean; $isUpdated: boolean; $isHighlighted: boolean }>`
  border-left: 1px solid ${colors.white};
  border-right: 1px solid ${colors.white};
  opacity: ${({ $isActive }: { $isActive: boolean }) => ($isActive ? 1 : 0.4)};
  ${({ $isHighlighted }: { $isHighlighted: boolean }) =>
    $isHighlighted
      ? css`
          height: 70px;
          border: 1px solid ${colors.primary};
          position: relative;
        `
      : ''}
`
export const StyledTableCell = styled(TableCell)<{ $isHighlighted: boolean }>`
  background-color: ${colors.white};
  background-clip: padding-box;
  border: none;
  padding: 8px 13px;
  border-top: 1px solid ${colors.lightGrey};
  cursor: pointer;
  ${(props: {
    $nowrap?: boolean
    $isNameCell?: boolean
    $cellWidth?: number
    $fixedWidth?: boolean
    $noPadding?: boolean
    $isContactCell?: boolean
    $isCentered?: boolean
    $actionable?: boolean
    $isDisabled: boolean
    $header: boolean
    $isHighlighted: boolean
    $fitContent: boolean
  }) => {
    let extraRules = ''
    if (props.$nowrap) {
      extraRules += 'white-space: nowrap; '
    }
    if (props.$cellWidth) {
      extraRules += `min-width: ${props.$cellWidth}px; `
    }
    if (props.$fixedWidth) {
      extraRules += `width: ${props.$cellWidth}px; `
      extraRules += `max-width: ${props.$cellWidth}px; `
    }
    if (props.$noPadding) {
      extraRules += 'padding: 0; '
    }
    if (props.$isNameCell) {
      extraRules += `position: sticky; left: 0; padding-left: 0; box-shadow: inset -1px 0 0 0 ${colors.lightGrey}; z-index: 2;`
    }
    if (props.$isContactCell) {
      extraRules += `position: sticky; right: -1px; box-shadow: inset 1px 0 0 0 ${colors.lightGrey}; z-index: 2; `
    }
    if (props.$actionable) {
      extraRules += ' cursor: pointer; '
    }
    if (props.$isDisabled) {
      extraRules += ' pointer-events: none;'
    }
    if (props.$isHighlighted) {
      extraRules += ` border-top: 1px solid ${colors.primary};
      border-bottom: 1px solid ${colors.primary};`
    }
    if (props.$header) {
      extraRules += ' border: none;'
    }
    if (props.$fitContent) {
      extraRules += ` max-width: fit-content; width: ${props.$cellWidth}px;`
    }

    return extraRules
  }};
`

export const TableCellInner = styled('div')`
  display: flex;
  align-items: center;
  color: ${colors.bodyText};
  background: ${colors.white};
`

export const TableHeadCellInner = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${colors.bodyText};
  background: ${colors.white};
  position: relative;
`

export const TableCellInnerContent = styled('div')`
  display: flex;
  align-items: center;
  flex: 1;
`

export const ColumnResizer = styled('div')`
  cursor: col-resize;
  width: 2px;
  height: 18px;
  background: ${colors.lightGrey};
  margin-left: 12px;
`
export const ColumnResizerDimmer = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vw;
  background: transparent;
  z-index: 11;
`

export const StyledSortButton = styled(SortButton)`
  margin-left: 5px;
` as typeof SortButton

export const StyledLogo = styled('img')`
  width: 26px;
  height: 26px;
`
export const StyledRoundWrapper = styled('span')`
  width: 26px;
  height: 26px;
  border: 2px solid ${colors.black};
  padding: 3px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const StyledProfileLogo = styled(ProfileLogo)`
  ${StyledAltLogoWrapper} {
    border: 2px solid ${colors.black};
    border-radius: 50%;
  }
`

export const TitleWrapper = styled(Link)`
  margin: 0 10px;
  color: ${colors.black};
  text-decoration: none;
  font-size: 14px;
  word-break: break-all;
  font-weight: 700;
  pointer-events: ${({ $isDisabled }: { $isDisabled?: boolean }) => ($isDisabled ? 'none ' : 'auto')};

  &:hover {
    color: ${colors.primaryDark};
  }
`

export const TitleCheckbox = styled(Checkbox)<{ $hasLeftOffset: boolean }>`
  ${({ $hasLeftOffset }: { $hasLeftOffset?: boolean }) => ($hasLeftOffset ? 'margin-left: 24px; ' : '')}
  margin-right: 4px;
  z-index: 2;
`

export const TitleHeading = styled(Typography)`
  line-height: 20px;
`

export const StyledColumnsConfigButtonWrapper = styled('div')`
  position: absolute;
  width: 10px;
  top: calc(50% - 13px);
  left: -10px;
`

export const StyledColumnsConfigButton = styled('span')`
  background-color: ${colors.primary};
  border-radius: 15px;
  color: ${colors.white};
  line-height: 19px;
  padding: 3px 5px;
  display: flex;
  gap: 3px;
  cursor: pointer;
`
export const StyledFilterButtonButton = styled('span')`
  background-color: ${({ $filtersApplied }: { $filtersApplied?: boolean }) => ($filtersApplied ? colors.white : colors.primary)};
  border-radius: 15px;
  color: ${colors.white};
  line-height: 19px;
  padding: 3px 5px;
  display: flex;
  cursor: pointer;
  opacity: ${({ $disabled }: { $disabled?: boolean }) => ($disabled ? '0.4' : '1')};
`

export const ShortlistPopoverWrapper = styled('div')`
  padding: ${componentSizes.smDistance};
  display: flex;
  flex-direction: column;
`

export const ShortlistPopoverButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
`

export const StyledNoteButton = styled(Button)`
  color: ${colors.bodyText};
  white-space: break-spaces;
  word-break: break-word;
  padding: 0;
  justify-content: flex-start;
  text-align: left;
  min-height: 20px;

  &:hover {
    color: ${colors.primary};
  }
` as typeof Button

export const NoteButtonLabel = styled('span')`
  color: #b6b6b6;
  &:hover {
    color: ${colors.primary};
  }
`
export const ContactButtonWrapper = styled(Box)`
  display: flex;
  margin-right: 8px;
`

export const HighlightedRowLabel = styled('div')`
  position: absolute;
  z-index: 2;
  left: -1px;
  bottom: -1px;
  padding: 3px 5px;
  background-color: ${colors.primary};
  font-size: 10px;
  font-weight: 500;
  color: ${colors.white};
`

export const ProfileDateTitle = styled('div')`
  color: ${colors.primary};
  font-weight: 600;
`

export const ProfileTitleContainer = styled('div')``

export const TitleContainer = styled('div')`
  display: flex;
  align-items: center;
  font-weight: 700;
`

export const SubtitleWrapper = styled(Typography)`
  margin: 0 10px;
  color: ${colors.bodyText};
  font-weight: 700;
  font-size: 12px;
`

export const CustomColumnCellContent = styled('div')`
  width: 100%;
`

export const CustomColumnCellInput = styled(InputText)`
  border: none;
  fieldset {
    border: none;
  }
`

export const StyledTitle = styled('div')`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
  width: fit-content;
`
export const AddNewCustomColumnButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  gap: 8px;
  cursor: pointer;
  color: ${colors.black};
  font-size: 14px;
  border-radius: 3px;
  border: 1px solid ${colors.lightGrey};
  width: 200px;
  margin-top: 5px;
`
